import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import { toast, errorToast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { HUBSPOT_BASE_URL } from "@/v2-backoffice-client/constants"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { useCustomerCompanySearchLazyQuery } from "@/v2-backoffice-client/customer/company/customer.company.search.query"
import { useCustomerCompanyLinkMutation } from "@/v2-backoffice-client/customer/company/customer.company.link.mutation"
import { useCustomerCompanyUnlinkMutation } from "@/v2-backoffice-client/customer/company/customer.company.unlink.mutation"

export type CustomerCompanyLinkDialogProps = DialogProps & {
  customer: CustomerFragment | null
}

// CustomerCompanyLinkDialog attaches a customer to its HubSpot company
// (search by name or org.nr → link) or detaches it. Linking stores the CRM
// snapshot, seeds EMPTY identity fields from the company, and pushes the
// app's identity to HubSpot (app-wins from the first moment). 1:1 both
// ways: companies already linked to another customer are shown but not
// linkable.
function CustomerCompanyLinkDialog(props: CustomerCompanyLinkDialogProps) {
  const { customer, open, onOpenChange, ...dialogProps } = props

  const [query, setQuery] = useState("")

  const [search, { data, loading: searching, error: searchError }] =
    useCustomerCompanySearchLazyQuery()

  const [linkCompany, { loading: linking, error: linkError }] =
    useCustomerCompanyLinkMutation({
      onCompleted(result) {
        toast({
          variant: "success",
          title: "HubSpot company linked",
          description: `${result.linkCustomerCompany.Name ?? "Customer"} is now linked — identity fields were pushed to HubSpot.`,
        })
        onOpenChange?.(false)
      },
    })

  const [unlinkCompany, { loading: unlinking, error: unlinkError }] =
    useCustomerCompanyUnlinkMutation({
      onCompleted() {
        toast({
          variant: "success",
          title: "HubSpot company unlinked",
          description: "The CRM snapshot was removed; the customer is kept.",
        })
        onOpenChange?.(false)
      },
    })

  errorToast(searchError, { title: "HubSpot company search failed" })

  if (!customer) return null

  const linkedCompany = customer.Company
  const results = data?.searchHubspotCompanies ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {linkedCompany ? "HubSpot company" : "Link HubSpot company"}
          </Dialog.Title>
          <Dialog.Description>
            {linkedCompany
              ? `${customer.Name ?? "This customer"} is linked to a HubSpot company.`
              : `Search HubSpot by company name or org.nr to link ${customer.Name ?? "this customer"}.`}
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage
          className="my-2 text-center"
          error={linkError ?? unlinkError}
        />

        {linkedCompany ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">
                {linkedCompany.Name ??
                  `Company ${linkedCompany.ExternalCompanyId}`}
              </span>
              <a
                className="truncate text-xs text-muted-foreground underline"
                href={`${HUBSPOT_BASE_URL}/${linkedCompany.ExternalCompanyId}`}
                target="_blank"
                rel="noreferrer"
              >
                Open in HubSpot ({linkedCompany.ExternalCompanyId})
              </a>
            </div>

            <Button
              type="button"
              variant="outline"
              isLoading={unlinking}
              onClick={() => {
                unlinkCompany({ variables: { customerId: customer.Id } })
              }}
            >
              Unlink
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (!query.trim()) return
                search({ variables: { query: query.trim(), limit: 20 } })
              }}
            >
              <Input
                autoFocus
                className="flex-1"
                placeholder="Company name or org.nr…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                }}
              />
              <Button type="submit" isLoading={searching}>
                Search
              </Button>
            </form>

            {data && results.length === 0 && !searching && (
              <p className="text-sm text-muted-foreground">
                No HubSpot companies matched.
              </p>
            )}

            {results.length > 0 && (
              <ul className="flex max-h-80 flex-col gap-1 overflow-auto">
                {results.map((company) => {
                  const isTaken =
                    company.LinkedCustomerId != null &&
                    company.LinkedCustomerId !== customer.Id
                  const detailLine = [
                    company.OrganizationNumber,
                    company.Domain,
                    company.City,
                  ]
                    .filter(Boolean)
                    .join(" · ")

                  return (
                    <li
                      key={company.ExternalCompanyId}
                      className="flex items-center justify-between gap-3 rounded-md border border-border p-2"
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">
                          {company.Name ??
                            `Company ${company.ExternalCompanyId}`}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {detailLine || company.ExternalCompanyId}
                        </span>
                      </div>

                      <Button
                        type="button"
                        size="tiny"
                        variant="outline"
                        disabled={isTaken}
                        isLoading={linking}
                        title={
                          isTaken
                            ? `Already linked to customer ${company.LinkedCustomerId}`
                            : undefined
                        }
                        onClick={() => {
                          linkCompany({
                            variables: {
                              customerId: customer.Id,
                              externalCompanyId: company.ExternalCompanyId,
                            },
                          })
                        }}
                      >
                        {isTaken ? "Taken" : "Link"}
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Close
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default CustomerCompanyLinkDialog
